/**
 * localStorage/sessionStorage helpers for AI mapping persistence.
 * All access is wrapped so storage failures never break the UI.
 */

import { aiMappingProposalStorageKey } from "./aiMappingTypes";

export interface AiMappingCostConfig {
  areaId: string;
  category: string;
  productId?: string | null;
  additionalCostId?: string | null;
  kind?: "detection_count" | "measured_area" | null;
  categories?: string[] | null;
  valueKind?: "count" | "linear_feet" | null;
  linearFeetPerUnit?: number | null;
}

const aiCostConfigsKey = (projectMapId: string): string =>
  `bitumio:ai-cost-configs:v2:${projectMapId}`;

export function loadAiCostConfigs(projectMapId: string): AiMappingCostConfig[] {
  try {
    const raw = window.localStorage.getItem(aiCostConfigsKey(projectMapId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as AiMappingCostConfig[]).map((config) =>
      config.kind === "detection_count"
        ? { ...config, category: config.category.replace(/^count:/, "") }
        : config
    );
  } catch {
    return [];
  }
}

export function upsertAiCostConfig(projectMapId: string, config: AiMappingCostConfig): void {
  try {
    const current = loadAiCostConfigs(projectMapId);
    const next = [
      ...current.filter(
        (existing) =>
          !(
            existing.areaId === config.areaId &&
            existing.category === config.category &&
            existing.additionalCostId === config.additionalCostId &&
            existing.productId === config.productId
          )
      ),
      config,
    ];
    window.localStorage.setItem(aiCostConfigsKey(projectMapId), JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
}

export function replaceAiCostConfigs(projectMapId: string, configs: AiMappingCostConfig[]): void {
  try {
    window.localStorage.setItem(aiCostConfigsKey(projectMapId), JSON.stringify(configs));
  } catch {
    /* ignore storage errors */
  }
}

export function removeAiCostConfig(
  projectMapId: string,
  category: string,
  options?: { areaIds?: string[]; additionalCostId?: string | null; productId?: string | null }
): void {
  try {
    const current = loadAiCostConfigs(projectMapId);
    const next = current.filter((existing) => {
      if (existing.category !== category) return true;
      if (options?.areaIds && !options.areaIds.includes(existing.areaId)) return true;
      if (
        options &&
        "additionalCostId" in options &&
        existing.additionalCostId !== options.additionalCostId
      )
        return true;
      if (options && "productId" in options && existing.productId !== options.productId)
        return true;
      return false;
    });
    window.localStorage.setItem(aiCostConfigsKey(projectMapId), JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
}

export function saveMapProposalId(projectMapId: string, proposalId: string): void {
  try {
    window.localStorage.setItem(aiMappingProposalStorageKey(projectMapId), proposalId);
  } catch {
    /* ignore storage errors */
  }
}

const aiMeasurementIdsKey = (projectMapId: string): string =>
  `bitumio:ai-measurement-feature-ids:${projectMapId}`;

/** featureIds of measurements written to the map by the AI "add to costs" flow. */
export function loadAiMeasurementFeatureIds(projectMapId: string): string[] {
  try {
    const raw = window.localStorage.getItem(aiMeasurementIdsKey(projectMapId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

export function saveAiMeasurementFeatureIds(projectMapId: string, featureIds: string[]): void {
  try {
    window.localStorage.setItem(aiMeasurementIdsKey(projectMapId), JSON.stringify(featureIds));
  } catch {
    /* ignore storage errors */
  }
}

type SyncKind = "MEASURED_AREA" | "DETECTION_COUNT";
type SyncValueKind = "LINEAR_FEET" | "COUNT";

export function toSyncKind(kind?: "detection_count" | "measured_area" | null): SyncKind | null {
  if (!kind) return null;
  return kind === "measured_area" ? "MEASURED_AREA" : "DETECTION_COUNT";
}

export function toSyncValueKind(valueKind?: "count" | "linear_feet" | null): SyncValueKind | null {
  if (!valueKind) return null;
  return valueKind === "linear_feet" ? "LINEAR_FEET" : "COUNT";
}

export function configToSyncItem(config: AiMappingCostConfig) {
  return {
    category: config.category,
    productId: config.productId as string,
    kind: toSyncKind(config.kind),
    categories: config.categories ?? null,
    valueKind: toSyncValueKind(config.valueKind),
    linearFeetPerUnit: config.linearFeetPerUnit ?? null,
  };
}
